import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { holidayService } from '@services/holidayService';
import { Holiday } from '@/types/holiday';
import { formatHolidayDate } from './holidayUtils';

export const HolidayListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [yearsLoaded, setYearsLoaded] = useState(false);
  const pageSize = 20;

  const loadYears = async (): Promise<number[]> => {
    try {
      const result = await holidayService.getYears();
      setYears(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar anos');
      return [];
    }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await holidayService.getAll({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
        // Ordem cronológica decrescente: do feriado mais recente para o mais antigo.
        orderBy: 'date',
        orderDirection: 'desc',
        year: selectedYear ?? undefined,
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Carrega os anos disponíveis e seleciona o mais recente por padrão.
  useEffect(() => {
    (async () => {
      const result = await loadYears();
      setSelectedYear(result.length > 0 ? result[0] : null);
      setYearsLoaded(true);
    })();
  }, []);

  // (Re)carrega a lista sempre que o ano selecionado ou a página mudarem.
  useEffect(() => {
    if (yearsLoaded) loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedYear, yearsLoaded]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadItems();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSelectYear = (year: number) => {
    if (year === selectedYear) return;
    setCurrentPage(1);
    setSelectedYear(year);
  };

  const handleDelete = async (id: number) => {
    try {
      await holidayService.delete(id);
      setDeleteConfirm(null);
      // Os anos podem mudar (ex.: excluído o último feriado de um ano).
      const updatedYears = await loadYears();
      if (selectedYear !== null && !updatedYears.includes(selectedYear)) {
        setSelectedYear(updatedYears.length > 0 ? updatedYears[0] : null);
      } else {
        loadItems();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Feriados</h1>
          <p className="text-slate-600">
            Gerencie os feriados utilizados nos cálculos de prazos e operações
          </p>
        </div>
        <PermissionGuard permission="holidays.write">
          <Button onClick={() => navigate('/settings/general/holidays/new')} icon={<Plus size={20} />}>
            Novo Feriado
          </Button>
        </PermissionGuard>
      </div>

      {/* Abas por ano (mais recente primeiro) */}
      {years.length > 0 && (
        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {years.map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => handleSelectYear(year)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                selectedYear === year
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      {/* Busca */}
      <Card>
        <CardContent>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                placeholder="Buscar por descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <Button onClick={handleSearch} icon={<Search size={18} />}>
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Data</th>
                <th className="text-left">Descrição</th>
                <th className="text-left">Abrangência</th>
                <th className="text-center">Situação</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-12 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-24 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-64 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-20 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-16 animate-pulse mx-auto" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-20 animate-pulse ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    Nenhum feriado encontrado
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.holiday_id}>
                    <TableCell className="font-mono text-sm">
                      {item.holiday_id}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatHolidayDate(item.date)}
                    </TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      {item.is_national_holiday ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Nacional</span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">Estadual / Municipal</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">Ativo</span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Inativo</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="holidays.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              navigate(`/settings/general/holidays/${item.holiday_id}/edit`)
                            }
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="holidays.write">
                          {deleteConfirm === item.holiday_id ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(item.holiday_id)}
                              >
                                Confirmar
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setDeleteConfirm(null)}
                              >
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.holiday_id)}
                              icon={<Trash2 size={14} />}
                            >
                              Excluir
                            </Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Total: {totalItems} registro(s)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
